import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWikiV2Client, wikiErrorStatus } from '../../../../hooks/useWikiV2';
import { useWikiQuery } from '../../shared/useWikiQueries';
import { wikiUi } from '../../shared/wikiUi';
import WikiIcon from '../../shared/WikiIcon';
import { WikiError, WikiLoading } from '../../shared/WikiStates';
import ProposalCard from './ProposalCard';
import DecidedLog from './DecidedLog';
import {
    BRAIN_OPS_TOASTS,
    brainErrorToast,
    brainOpsKeys,
    brainOpsPaths,
    invalidateBrain,
    useBrainAccess,
} from './brainOpsShared';
import './brainOps.css';
import './brainReview.css';

/**
 * AI Brain › Review queue (E2 §1). Policy banner, pending proposal cards
 * (ProposalCard), the dashed empty state and the DECIDED log.
 *
 * Contract (contracts/BE-brain.md §4): GET /wiki/brain/proposals →
 * `{pending, decided, pending_count}`; accept `{wording?}` / reject
 * `{reason}` / revert / replay (server-side, no body). Wiki admins decide;
 * Editors (readOnly) see every card with a lock line and no buttons.
 *
 * Props from BrainPage: `{ role, canManage, readOnly }` (see useBrainAccess).
 */

function proposalId(proposal) {
    return proposal?.proposal_id || proposal?.id;
}

function localDecided(proposal, outcome, status) {
    return {
        proposal_id: proposalId(proposal),
        title: proposal.title,
        outcome,
        who: 'You',
        when: 'Just now',
        at: new Date().toISOString(),
        status,
        can_revert: status === 'accepted',
    };
}

export default function BrainReviewTab(props) {
    const { canManage, readOnly } = useBrainAccess(props);
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const query = useWikiQuery(brainOpsKeys.proposals(), brainOpsPaths.proposals);
    const [edits, setEdits] = useState({});
    const [rejecting, setRejecting] = useState(null);
    const [busy, setBusy] = useState({});

    const patchQueue = useCallback((fn) => {
        queryClient.setQueryData(brainOpsKeys.proposals(), (old) => (old ? fn(old) : old));
    }, [queryClient]);

    const setBusyFor = useCallback((id, kind) => {
        setBusy((prev) => {
            const next = { ...prev };
            if (kind) next[id] = kind; else delete next[id];
            return next;
        });
    }, []);

    const clearEdit = useCallback((id) => {
        setEdits((prev) => {
            if (!(id in prev)) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    /** Remove a decided proposal from the queue now (the prototype is instant), then refetch. */
    const settle = useCallback((proposal, outcome, status, pendingCount) => {
        const id = proposalId(proposal);
        patchQueue((old) => {
            const pending = (old.pending || []).filter((p) => proposalId(p) !== id);
            return {
                ...old,
                pending,
                pending_count: typeof pendingCount === 'number' ? pendingCount : pending.length,
                decided: [localDecided(proposal, outcome, status), ...(old.decided || [])],
            };
        });
        clearEdit(id);
    }, [patchQueue, clearEdit]);

    const handleApprove = useCallback(async (proposal) => {
        const id = proposalId(proposal);
        if (!canManage || busy[id]) return;
        const edited = edits[id];
        const body = edited !== undefined && edited !== null ? { wording: String(edited).trim() } : {};
        setBusyFor(id, 'accept');
        try {
            const res = await client.post(brainOpsPaths.accept(id), body);
            settle(proposal, 'Approved', 'accepted', res?.pending_count);
            wikiUi.toast(BRAIN_OPS_TOASTS.approved);
            invalidateBrain(queryClient);
        } catch (error) {
            wikiUi.toast(brainErrorToast(error, 'Approving a change'));
            // 409 (already decided / failed on the new wording): show the server's state.
            if (wikiErrorStatus(error) === 409) invalidateBrain(queryClient);
        } finally {
            setBusyFor(id, null);
        }
    }, [busy, canManage, client, edits, queryClient, setBusyFor, settle]);

    const handleReject = useCallback(async (proposal, reason) => {
        const id = proposalId(proposal);
        if (!canManage || busy[id]) return;
        setBusyFor(id, 'reject');
        try {
            const res = await client.post(brainOpsPaths.reject(id), { reason });
            setRejecting(null);
            settle(proposal, `Rejected · ${reason.toLowerCase()}`, 'rejected', res?.pending_count);
            wikiUi.toast(reason === 'Redraft it' ? BRAIN_OPS_TOASTS.rejectedRedraft : BRAIN_OPS_TOASTS.rejected);
            invalidateBrain(queryClient);
        } catch (error) {
            wikiUi.toast(brainErrorToast(error, 'Rejecting a change'));
            if (wikiErrorStatus(error) === 409) invalidateBrain(queryClient);
        } finally {
            setBusyFor(id, null);
        }
    }, [busy, canManage, client, queryClient, setBusyFor, settle]);

    const handleReplay = useCallback(async (proposal) => {
        const id = proposalId(proposal);
        if (!canManage || busy[id]) return;
        setBusyFor(id, 'replay');
        try {
            const res = await client.post(brainOpsPaths.replay(id));
            const next = res?.proposal;
            if (next) {
                patchQueue((old) => ({
                    ...old,
                    pending: (old.pending || []).map((p) => (proposalId(p) === id ? { ...p, ...next } : p)),
                }));
            }
            invalidateBrain(queryClient);
        } catch (error) {
            wikiUi.toast(brainErrorToast(error, 'Running the replay test'));
        } finally {
            setBusyFor(id, null);
        }
    }, [busy, canManage, client, patchQueue, queryClient, setBusyFor]);

    const handleRevert = useCallback(async (row) => {
        const id = row.proposal_id;
        if (!canManage || !id || busy[id]) return;
        setBusyFor(id, 'revert');
        try {
            const res = await client.post(brainOpsPaths.revert(id));
            patchQueue((old) => ({
                ...old,
                pending_count: typeof res?.pending_count === 'number' ? res.pending_count : old.pending_count,
                decided: (old.decided || []).map((d) => (d.proposal_id === id
                    ? { ...d, outcome: 'Reverted', status: 'reverted', can_revert: false }
                    : d)),
            }));
            wikiUi.toast(BRAIN_OPS_TOASTS.reverted);
            invalidateBrain(queryClient);
        } catch (error) {
            wikiUi.toast(brainErrorToast(error, 'Reverting a change'));
            if (wikiErrorStatus(error) === 409) invalidateBrain(queryClient);
        } finally {
            setBusyFor(id, null);
        }
    }, [busy, canManage, client, patchQueue, queryClient, setBusyFor]);

    const startEdit = useCallback((proposal) => {
        const id = proposalId(proposal);
        setEdits((prev) => ({ ...prev, [id]: proposal.draft || '' }));
    }, []);

    const changeEdit = useCallback((id, value) => {
        setEdits((prev) => ({ ...prev, [id]: value }));
    }, []);

    const data = query.data || {};
    const pending = Array.isArray(data.pending) ? data.pending : [];
    const decided = Array.isArray(data.decided) ? data.decided : [];

    let body;
    if (query.isLoading) {
        body = <div className="wk-card"><WikiLoading label="Loading the review queue…" /></div>;
    } else if (query.isError) {
        body = <div className="wk-card"><WikiError error={query.error} onRetry={() => query.refetch()} /></div>;
    } else {
        body = (
            <>
                {pending.length === 0 ? (
                    <div className="wk-bro-empty" data-testid="brain-queue-empty">
                        Nothing waiting. New proposals appear here when feedback repeats.
                    </div>
                ) : (
                    <div className="wk-brq-list" data-testid="brain-queue-list">
                        {pending.map((proposal) => {
                            const id = proposalId(proposal);
                            return (
                                <ProposalCard
                                    key={id}
                                    proposal={proposal}
                                    canManage={canManage}
                                    readOnly={readOnly}
                                    draftEdit={id in edits ? edits[id] : null}
                                    rejectOpen={rejecting === id}
                                    busy={busy[id] || null}
                                    onStartEdit={startEdit}
                                    onEditChange={changeEdit}
                                    onOpenReject={setRejecting}
                                    onCancelReject={() => setRejecting(null)}
                                    onReject={handleReject}
                                    onApprove={handleApprove}
                                    onReplay={handleReplay}
                                />
                            );
                        })}
                    </div>
                )}
                <DecidedLog rows={decided} canManage={canManage} busy={busy} onRevert={handleRevert} />
            </>
        );
    }

    return (
        <div className="wk-brq wk-stack" data-testid="brain-review-tab">
            <div className="wk-brq-banner" data-testid="brain-queue-policy">
                <WikiIcon name="shield-check" size={17} className="wk-brq-banner-icon" />
                <span>
                    The Brain only proposes a change when something repeats: the same mistake <b>2 times</b> becomes
                    a guardrail, the same unsure spot <b>3 times</b> becomes a new check. Every draft is replayed
                    against past questions first. It must catch at least 80% of its own cases and fire on no more
                    than 20% of unrelated ones.
                </span>
            </div>
            {body}
        </div>
    );
}
